/** Manual low-bandwidth control for one monitoring room. */

import type { Room } from '@studio/models/room';
import type { ReactElement } from 'react';
import { useCallback, useEffect, useState } from 'react';
// Archetype, not raw Switch+Thumb: the thumb's translate variant is driven by
// the `group` class the archetype puts on the root — bare primitives render a
// track that changes color while the knob never moves.
// Per rule: investigate-before-adding (ai.knowledge/global) — every other
// switch call site composes via Archetype.
import { Archetype as Switch } from '@studio/design-system/switch';
import { useRoomLiveKit } from '#~/livekit/hooks.js';
import type { GovernorState } from '#~/livekit/bandwidth-governor.js';

const STATE_COLORS: Record<GovernorState | 'disabled', string> = {
  normal: 'text-success',
  congested: 'text-warning',
  disabled: 'text-muted'
};

const STATE_LABELS: Record<GovernorState | 'disabled', string> = {
  normal: 'Normal',
  congested: 'Congested',
  disabled: 'Disabled'
};

type Props = {
  roomId: Room['id'] | undefined;
};

/** Lets an operator override automatic bandwidth management for the current room. */
export const BandwidthToggle = ({ roomId }: Props): ReactElement => {
  const liveKit = useRoomLiveKit(roomId);
  const [governorState, setGovernorState] = useState<GovernorState | 'disabled'>('disabled');
  const [manualMode, setManualMode] = useState(false);

  useEffect(() => {
    if (!liveKit) {
      setGovernorState('disabled');
      setManualMode(false);
      return;
    }

    setGovernorState(liveKit.getBandwidthGovernorState());
    setManualMode(liveKit.isManualLowBandwidthMode());

    const handleGovernorState = (state: GovernorState): void => setGovernorState(state);
    const handleManualMode = (enabled: boolean): void => setManualMode(enabled);

    liveKit.on('bandwidthGovernorStateChanged', handleGovernorState);
    liveKit.on('bandwidthManualModeChanged', handleManualMode);

    // Event subscriptions avoid polling every room and preserve room ownership.
    return () => {
      liveKit.off('bandwidthGovernorStateChanged', handleGovernorState);
      liveKit.off('bandwidthManualModeChanged', handleManualMode);
    };
  }, [liveKit]);

  const handleToggle = useCallback(
    (checked: boolean) => {
      liveKit?.setManualLowBandwidthMode(checked);
    },
    [liveKit]
  );

  if (governorState === 'disabled') {
    return <></>;
  }

  return (
    <div className='mb-12 flex items-center justify-between rounded-4 bg-base-200 p-12'>
      <div className='flex flex-col gap-2'>
        <div className='flex items-center gap-8'>
          <span className='text-sm font-medium'>Low Bandwidth Mode</span>
          {manualMode && (
            <span className='bg-primary/20 py-1 rounded px-4 text-2xs font-demibold text-primary'>
              Manual
            </span>
          )}
        </div>
        <div className='flex items-center gap-4 text-2xs'>
          <span className='text-muted'>Auto-detect:</span>
          <span className={STATE_COLORS[governorState]}>{STATE_LABELS[governorState]}</span>
        </div>
      </div>
      <Switch
        aria-label='Toggle manual low-bandwidth mode'
        checked={manualMode}
        data-stag='interactive:control-room.monitoring.bandwidth-toggle'
        onCheckedChange={handleToggle}
      />
    </div>
  );
};
